import { enableProdMode, LOCALE_ID, ErrorHandler, importProvidersFrom } from '@angular/core';
import { environment } from '@env/environment';
import { loadTranslations } from '@angular/localize';
import { initAndGetLocalizationData } from 'locale-loader';
import 'zone.js';
import { TranslateHelper } from '@app/shared/utils/static-helpers/translate';
import { OKTA_CONFIG, OktaAuthModule } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { LocalizationService } from './app/core/services/utils/localization/localization.service';
import { AuthGuard, ErrorHandlerService, ScriptService, TokenInterceptor } from './app/core/services';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { APP_ROUTES } from './app/app-routing';
import { localStorageSyncReducer } from '@app/store/store.helper';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { AppComponent } from './app/app.component';
import { provideRouter, withPreloading, TitleStrategy, NoPreloading } from '@angular/router';
import { MetaReducer, provideStore, StoreRootModule } from '@ngrx/store';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { EffectsModule, provideEffects } from '@ngrx/effects';
import { appReducers, appEffects, appFacades } from '@app/store';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { AnalyticsInterceptor } from '@app/core/services/tracking';
import { CurrencyPipe, DatePipe, LowerCasePipe, TitleCasePipe } from '@angular/common';
import { MatDateFnsModule, provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { CookieService } from 'ngx-cookie-service';
import { sharedPipes } from '@app/shared';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef, MatDialogConfig } from '@angular/material/dialog';
import { LocaleDateFormatHelper } from '@app/shared/utils/static-helpers/locale-date-format-helper';
import { CustomDateAdapter } from '@app/shared/utils/static-helpers/custom-date-adapter';
import { PageTitleStrategy } from '@app/core/services/title/page-title.strategy';
import '@rc-df/ds-web-components/rc-button/src/RcButton';
import '@rc-df/ds-web-components/rc-badge/src/RcBadge';
import '@rc-df/ds-web-components/rc-input/src/RcInput';
import '@rc-df/ds-web-components/rc-icon/src/RcIcon';
import '@rc-df/ds-web-components/rc-link/src/RcLink';
import '@rc-df/ds-web-components/rc-loader/src/RcLoader';
import '@rc-df/ds-web-components/rc-alert/src/RcAlert';
import '@rc-df/ds-web-components/rc-toast/src/RcToast';
import { editIconsPath } from '@rc-df/ds-web-components/rc-icon/src';

editIconsPath('/assets/icons/rc-df/sprite.svg');

const oktaAuth = new OktaAuth(getOktaConfig());
function getOktaConfig() {
  const isIE = navigator.userAgent.indexOf('MSIE') !== -1 || navigator.appVersion.indexOf('Trident/') > -1;
  return {
    ...environment.oktaConfig,
    pkce: !isIE,
  };
}

if (environment.production) {
  enableProdMode();
}

export const metaReducers: Array<MetaReducer<any, any>> = [localStorageSyncReducer];

initAndGetLocalizationData()
  .then((data) => {
    TranslateHelper.saveTranslation(data);
    loadTranslations(data.translations);
    TranslateHelper.saveTranslationBackup(data);
  })
  .catch((e) => {
    loadTranslations(TranslateHelper.getTranslationBackup().translations);
    console.error(e);
  })
  .finally(() => {
    bootstrapApplication(AppComponent, {
      providers: [
        ...appFacades,
        provideHttpClient(withInterceptors([]), withInterceptorsFromDi()),
        provideAnimations(),
        importProvidersFrom(OktaAuthModule),
        AuthGuard,
        {
          provide: HTTP_INTERCEPTORS,
          useClass: TokenInterceptor,
          multi: true,
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: AnalyticsInterceptor,
          multi: true,
        },
        CookieService,
        ScriptService,
        /* Pipes */
        ...sharedPipes,
        DatePipe,
        TitleCasePipe,
        LowerCasePipe,
        CurrencyPipe,
        provideDateFnsAdapter(),
        MatDateFnsModule,
        MatDialog,
        { provide: MAT_DIALOG_DATA, useValue: {} },
        {
          provide: MatDialogRef,
          useValue: {},
        },
        { provide: MatDialogConfig, useValue: {} },
        {
          provide: TitleStrategy,
          useClass: PageTitleStrategy,
        },
        { provide: OKTA_CONFIG, useValue: { oktaAuth } },
        { provide: LOCALE_ID, useValue: LocalizationService.initLanguage },
        { provide: ErrorHandler, useClass: ErrorHandlerService },
        {
          provide: MAT_DATE_LOCALE,
          useValue: LocalizationService.initLanguage,
        },
        {
          provide: MAT_DATE_FORMATS,
          useFactory: (localeDateFormatHelper: LocaleDateFormatHelper) => localeDateFormatHelper.getCustomDateFormats(), // Get dynamic formats
          deps: [LocaleDateFormatHelper],
        },
        { provide: DateAdapter, useClass: CustomDateAdapter },
        importProvidersFrom(StoreRootModule, EffectsModule.forRoot(), StoreRouterConnectingModule.forRoot()),
        provideStore(appReducers, { metaReducers }),
        provideEffects(appEffects),
        provideStoreDevtools({
          maxAge: 25, // Retains last 25 states
          logOnly: environment.production, // Restrict extension to log-only mode
          connectInZone: true,
        }),
        provideRouter(APP_ROUTES, withPreloading(NoPreloading)),
      ],
    }).catch((err) => console.error(err));
  });
